/**
 * Lightweight, additive image-URL optimizer.
 *
 * Goal: when an Unsplash URL is rendered in a public-facing card / gallery /
 * listing, ask Unsplash's CDN to deliver a smaller WebP-encoded JPEG instead
 * of the raw high-quality JPEG. Saves significant bandwidth on mobile.
 *
 * SAFE BY DESIGN
 *  - Non-Unsplash URLs are returned unchanged (CMS uploads, /api/media, etc.)
 *  - Relative URLs are returned unchanged
 *  - Empty / null inputs are returned as-is
 *  - Existing query params on Unsplash URLs (e.g. ?w=800) are preserved;
 *    we only add the ones that aren't already present
 *  - No dependency, no DB write, no schema change
 *
 * Usage:
 *   <img src={getOptimizedImageUrl(normalizeImageUrl(pkg.image))} />
 */

const UNSPLASH_HOSTS = ['images.unsplash.com', 'plus.unsplash.com'];

// Params we want to add to every Unsplash URL (only if not already present).
const OPTIMIZATION_PARAMS = {
  auto: 'format',
  fm: 'webp',
  q: '70',
};

const isUnsplashUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  if (!url.startsWith('http')) return false;
  try {
    const parsed = new URL(url);
    return UNSPLASH_HOSTS.includes(parsed.hostname);
  } catch {
    return false;
  }
};

/**
 * Optimize a single image URL for delivery.
 * Currently only transforms Unsplash URLs; everything else is passed through
 * unchanged. Always returns a string (or the original falsy input).
 */
export const getOptimizedImageUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (!isUnsplashUrl(url)) return url;

  try {
    const parsed = new URL(url);
    Object.entries(OPTIMIZATION_PARAMS).forEach(([key, value]) => {
      if (!parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, value);
      }
    });
    return parsed.toString();
  } catch {
    // Defensive: if anything goes wrong, return the original URL untouched.
    return url;
  }
};

/**
 * Build a single Unsplash variant of `url` at exactly `width` px.
 * Returns the original URL untouched for non-Unsplash inputs (CMS uploads,
 * relative URLs, `/api/media/...`, etc.) so callers can use the result as
 * either a `src` OR a `srcSet` candidate without branching.
 */
export const getOptimizedImageUrlAtWidth = (url, width) => {
  if (!url || typeof url !== 'string') return url;
  if (!isUnsplashUrl(url) || !Number.isFinite(width) || width <= 0) {
    return getOptimizedImageUrl(url);
  }
  try {
    const parsed = new URL(url);
    Object.entries(OPTIMIZATION_PARAMS).forEach(([key, value]) => {
      if (!parsed.searchParams.has(key)) parsed.searchParams.set(key, value);
    });
    // Always overwrite `w` even if it was present — the caller is
    // explicitly asking for THIS width.
    parsed.searchParams.set('w', String(width));
    return parsed.toString();
  } catch {
    return getOptimizedImageUrl(url);
  }
};

/**
 * Standard responsive-image width ladder used by <DeferImage>. The ladder
 * is intentionally short — covers small-mobile through 2x-desktop with
 * enough granularity for the browser to pick a sensible variant without
 * fetching a 200KB image for a 320px viewport. Powers of ~1.5x.
 */
export const DEFAULT_WIDTH_LADDER = [320, 640, 768, 1024, 1440, 1920];

/**
 * Build a `srcSet` string from a single source URL. Returns an empty
 * string for non-Unsplash URLs so callers know there's no responsive
 * variants to offer (and they should fall back to a plain `src`).
 *
 *   buildSrcSet(url) → "https://...?w=320 320w, https://...?w=640 640w, ..."
 */
export const buildSrcSet = (url, widths = DEFAULT_WIDTH_LADDER) => {
  if (!isUnsplashUrl(url)) return '';
  return widths
    .map(w => `${getOptimizedImageUrlAtWidth(url, w)} ${w}w`)
    .join(', ');
};

export default getOptimizedImageUrl;
